import { ChipType } from 'components/@extended/StatusChip';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import NumberFormat from 'react-number-format';
import { InvoiceTimecardsAndExpensesDto } from 'types/invoice-details';
import {
    EditIntentTypeCode,
    InvoiceCorrectionDto,
    InvoiceData,
    InvoiceDataListItem,
    InvoiceStatusType,
    InvoiceTotals,
    InvoiceTransitionDto,
    InvoiceType,
} from 'types/invoices';
import { PricingLimitationType } from 'types/matters';
import { NamedEntityMinDto } from 'types/other';
import { isDateEarlierThanToday } from 'utils/date';
import { DEFAULT_DATE_FORMAT } from 'utils/parseDate';

type BudgetInfoMatter = {
    pricingLimitationTypeCode?: string;
    pricelistCurrencyTypeCode?: string;
    // Nullable: list payloads (e.g. the case list) send these as explicit nulls. Every read below is
    // behind a truthiness check, so null narrows away before the value is formatted.
    capUnitTypeCode?: string | null;
    capLimit?: number | null;
    fixedFee?: number | null;
};

const getUniqueEmployees = (employees: (NamedEntityMinDto | null | undefined)[]): NamedEntityMinDto[] => {
    const uniqueMap = new Map<number, NamedEntityMinDto>();

    employees.forEach((employee) => {
        if (employee) {
            uniqueMap.set(employee.id, employee);
        }
    });

    return Array.from(uniqueMap.values());
};

export const getResponsibleEmployeesByState = (invoice: InvoiceData): NamedEntityMinDto[] => {
    switch (invoice.invoiceStatusTypeCode) {
        case InvoiceStatusType.DRAFT:
            return getUniqueEmployees((invoice.invoiceMatters || []).map((item) => item.responsibleEmployee));
        case InvoiceStatusType.REVIEWED:
            return getUniqueEmployees((invoice.invoiceMatters || []).map((item) => item.supervisedByEmployee));
        default:
            return [];
    }
};

export const getAllResponsibleEmployees = (invoice: InvoiceData): NamedEntityMinDto[] => {
    const allEmployees = (invoice.invoiceMatters || []).flatMap((item) => [
        item.responsibleEmployee,
        item.supervisedByEmployee,
    ]);

    return getUniqueEmployees(allEmployees);
};

/** An invoice's page: under the contact's or the case's tab when the list sits in one, else its own. */
export const invoicePath = (id: number, { contactId, matterId }: { contactId?: string; matterId?: string }): string =>
    contactId
        ? `/contact/${contactId}/invoice/${id}`
        : matterId
          ? `/matter/${matterId}/invoice/${id}`
          : `/invoice/${id}`;

export const getInvoiceTypeLetter = (invoiceTypeCode: string | undefined): string => {
    switch (invoiceTypeCode) {
        case InvoiceType.CORRECTION:
            return 'R';
        case InvoiceType.CREDIT:
            return 'C';
        case InvoiceType.DEBIT:
            return 'D';
        case InvoiceType.PREPAYMENT:
            return 'P';
        default:
            return invoiceTypeCode?.charAt(0) ?? '';
    }
};

export const getResponsibleOrSupervisingText = (invoiceStatusTypeCode: InvoiceStatusType): string | undefined => {
    switch (invoiceStatusTypeCode) {
        case InvoiceStatusType.DRAFT:
            return 'invoices.responsible-persons';
        case InvoiceStatusType.REVIEWED:
            return 'invoices.supervising-partners';
        default:
            return undefined;
    }
};

type InvoiceTransitionAlertMessageProps = {
    messageId: string;
    lastTransition: InvoiceTransitionDto;
};

export const InvoiceTransitionAlertMessage = ({ messageId, lastTransition }: InvoiceTransitionAlertMessageProps) => (
    <>
        <div className="font-bold">
            <FormattedMessage id={messageId} values={{ name: lastTransition.changedByEmployee?.name || '-' }} />
        </div>
        {lastTransition.comment && (
            <div className="font-medium">
                <FormattedMessage id="invoices.alerts.reason" values={{ reason: lastTransition.comment }} />
            </div>
        )}
        {lastTransition.matter && (
            <div className="font-medium">
                <FormattedMessage id="invoices.alerts.case" values={{ case: lastTransition.matter.name }} />
            </div>
        )}
    </>
);

export const getInvoiceStatusChipType = (invoiceStatusTypeCode: string): ChipType => {
    switch (invoiceStatusTypeCode) {
        case InvoiceStatusType.PAID:
            return 'success';

        case InvoiceStatusType.SENT:
        case InvoiceStatusType.POSTPONED:
            return 'neutral';

        case InvoiceStatusType.APPROVED:
            return 'warning';

        case InvoiceStatusType.REVIEWED:
            return 'reviewed';

        case InvoiceStatusType.FINALIZED:
            return 'finalized';

        case InvoiceStatusType.CREDITED:
            return 'credited';

        default:
            return 'outlined';
    }
};

/** The date of the rate that took the invoice into its base currency: from the payment currency's, or straight. */
export const getBaseConversionDate = (
    invoice: Pick<
        InvoiceData,
        | 'invoiceCurrencyTypeCode'
        | 'paymentCurrencyTypeCode'
        | 'baseCurrencyConversionDate'
        | 'paymentCurrencyConversionDate'
    >,
    isConvertBaseFromPaymentCurrency: boolean
) =>
    isConvertBaseFromPaymentCurrency || invoice.invoiceCurrencyTypeCode === invoice.paymentCurrencyTypeCode
        ? invoice.baseCurrencyConversionDate
        : invoice.paymentCurrencyConversionDate;

export const getInvoiceTotalsAmounts = (totals?: InvoiceTotals) => {
    const totalWithoutVat = totals?.invoiceCurrency.invoice.totalWithoutVat ?? 0;
    const totalVat = totals?.invoiceCurrency.invoice.vat ?? 0;
    const totalWithVat = totals?.invoiceCurrency.invoice.totalWithVat ?? 0;

    const totalWithoutVatPaymentCurrency = totals?.paymentCurrency.invoice.totalWithoutVat ?? 0;
    const totalVatPaymentCurrency = totals?.paymentCurrency.invoice.vat ?? 0;
    const totalWithVatPaymentCurrency = totals?.paymentCurrency.invoice.totalWithVat ?? 0;

    const totalWithoutVatBaseCurrency = totals?.baseCurrency.invoice.totalWithoutVat ?? 0;
    const totalVatBaseCurrency = totals?.baseCurrency.invoice.vat ?? 0;
    const totalWithVatBaseCurrency = totals?.baseCurrency.invoice.totalWithVat ?? 0;

    return {
        totalWithVat,
        totalWithoutVat,
        totalWithVatPaymentCurrency,
        totalWithoutVatPaymentCurrency,
        totalWithoutVatBaseCurrency,
        totalWithVatBaseCurrency,
        totalVat,
        totalVatPaymentCurrency,
        totalVatBaseCurrency,
    };
};

export const getInvoiceDeltaAmounts = (delta?: InvoiceTotals) => {
    const deltaFees = delta?.invoiceCurrency.fees.totalWithoutVat ?? 0;
    const deltaExpenses =
        (delta?.invoiceCurrency.expensesWithoutVat.totalWithoutVat ?? 0) +
        (delta?.invoiceCurrency.expensesWithVat.totalWithoutVat ?? 0);
    const deltaTotalWithoutVat = delta?.invoiceCurrency.invoice.totalWithoutVat ?? 0;
    const deltaVat = delta?.invoiceCurrency.invoice.vat ?? 0;
    const deltaTotalWithVat = delta?.invoiceCurrency.invoice.totalWithVat ?? 0;

    const deltaFeesPaymentCurrency = delta?.paymentCurrency.fees.totalWithoutVat ?? 0;
    const deltaExpensesPaymentCurrency =
        (delta?.paymentCurrency.expensesWithoutVat.totalWithoutVat ?? 0) +
        (delta?.paymentCurrency.expensesWithVat.totalWithoutVat ?? 0);
    const deltaTotalWithoutVatPaymentCurrency = delta?.paymentCurrency.invoice.totalWithoutVat ?? 0;
    const deltaVatPaymentCurrency = delta?.paymentCurrency.invoice.vat ?? 0;
    const deltaTotalWithVatPaymentCurrency = delta?.paymentCurrency.invoice.totalWithVat ?? 0;

    const deltaFeesBaseCurrency = delta?.baseCurrency.fees.totalWithoutVat ?? 0;
    const deltaExpensesBaseCurrency =
        (delta?.baseCurrency.expensesWithoutVat.totalWithoutVat ?? 0) +
        (delta?.baseCurrency.expensesWithVat.totalWithoutVat ?? 0);
    const deltaTotalWithoutVatBaseCurrency = delta?.baseCurrency.invoice.totalWithoutVat ?? 0;
    const deltaVatBaseCurrency = delta?.baseCurrency.invoice.vat ?? 0;
    const deltaTotalWithVatBaseCurrency = delta?.baseCurrency.invoice.totalWithVat ?? 0;

    return {
        deltaFees,
        deltaExpenses,
        deltaTotalWithoutVat,
        deltaVat,
        deltaTotalWithVat,
        deltaFeesPaymentCurrency,
        deltaExpensesPaymentCurrency,
        deltaTotalWithoutVatPaymentCurrency,
        deltaVatPaymentCurrency,
        deltaTotalWithVatPaymentCurrency,
        deltaFeesBaseCurrency,
        deltaExpensesBaseCurrency,
        deltaTotalWithoutVatBaseCurrency,
        deltaVatBaseCurrency,
        deltaTotalWithVatBaseCurrency,
    };
};

export const getBudgetInfo = (matter: BudgetInfoMatter, cashOnSeparateLine = false) => {
    if (matter.pricingLimitationTypeCode === PricingLimitationType.FIXED_ONETIME && matter.fixedFee) {
        if (cashOnSeparateLine) {
            return (
                <div className="flex flex-col items-end">
                    <span>
                        <FormattedMessage id="invoices.fixed-fee" />
                    </span>
                    <MoneyDisplay amount={matter.fixedFee} currency={matter.pricelistCurrencyTypeCode} />
                </div>
            );
        }
        return (
            <>
                <span className="mr-[5px]">
                    <FormattedMessage id="invoices.fixed-fee" />
                </span>
                <MoneyDisplay amount={matter.fixedFee} currency={matter.pricelistCurrencyTypeCode} />
            </>
        );
    }

    if (matter.pricingLimitationTypeCode === PricingLimitationType.HOURLY_CAPPED && matter.capLimit) {
        if (matter.capUnitTypeCode === 'CASH' || matter.capUnitTypeCode === 'DEFAULT') {
            if (cashOnSeparateLine) {
                return (
                    <div className="flex flex-col items-end">
                        <span>
                            <FormattedMessage id="invoices.cap" />
                        </span>
                        <MoneyDisplay amount={matter.capLimit} currency={matter.pricelistCurrencyTypeCode} />
                    </div>
                );
            }
            return (
                <>
                    <span className="mr-[5px]">
                        <FormattedMessage id="invoices.cap" />
                    </span>
                    <MoneyDisplay amount={matter.capLimit} currency={matter.pricelistCurrencyTypeCode} />
                </>
            );
        } else {
            return (
                <>
                    <span className="mr-[5px]">
                        <FormattedMessage id="invoices.cap" />
                    </span>
                    <NumberFormat
                        displayType="text"
                        fixedDecimalScale
                        decimalScale={1}
                        suffix={`\u00a0h`}
                        value={matter.capLimit}
                    />
                </>
            );
        }
    }

    return '';
};

const isInvoiceCorrection = (invoice: InvoiceDataListItem): boolean =>
    invoice.invoiceTypeCode === InvoiceType.CORRECTION;

// Invoice total before prepayment deduction. Corrections never have prepayment, so the delta
// totalWithVat already represents the full amount; for everything else use totalAmountInPaymentCurrency
// rather than finalPayableAmountInPaymentCurrency (which subtracts prepayment).
export const getInvoiceTotalBeforePrepayment = (invoice: InvoiceDataListItem): string | number => {
    if (isInvoiceCorrection(invoice)) {
        return invoice.invoiceDeltaTotals?.paymentCurrency?.invoice.totalWithVat ?? 0;
    }
    return invoice.totalAmountInPaymentCurrency;
};

export const isDebtReminderEligible = (invoice: InvoiceDataListItem) => {
    const { invoiceTypeCode, invoiceStatusTypeCode, dueDate, outstandingBalanceInPaymentCurrency } = invoice;
    if (
        invoiceTypeCode === InvoiceType.DEBIT &&
        invoiceStatusTypeCode === InvoiceStatusType.SENT &&
        dueDate &&
        isDateEarlierThanToday(dueDate) &&
        parseFloat(outstandingBalanceInPaymentCurrency) > 0
    ) {
        return true;
    }

    return false;
};

// Collect all corrections from the chain recursively
export const collectCorrections = (correctionInvoice?: InvoiceCorrectionDto): InvoiceCorrectionDto[] => {
    if (!correctionInvoice) return [];

    const corrections: InvoiceCorrectionDto[] = [correctionInvoice];
    let currentCorrection = correctionInvoice;

    while (currentCorrection.correctionInvoice) {
        corrections.push(currentCorrection.correctionInvoice);
        currentCorrection = currentCorrection.correctionInvoice;
    }

    return corrections;
};

type MovableItem = {
    editIntentTypeCode?: EditIntentTypeCode | null;
    newMatter?: { id: number } | null;
};

export const isItemMovingOutOfInvoice = (item: MovableItem, invoiceMatterIds: ReadonlySet<number>): boolean =>
    item.editIntentTypeCode === EditIntentTypeCode.MOVE_TO_ANOTHER_CASE &&
    item.newMatter?.id != null &&
    !invoiceMatterIds.has(item.newMatter.id);

export const getInvoiceMatterIds = (invoiceData?: Pick<InvoiceData, 'invoiceMatters'> | null): Set<number> =>
    new Set((invoiceData?.invoiceMatters ?? []).map((im) => im.matterId));

export const getStayingTimecardsAndExpenses = (
    invoiceData: Pick<InvoiceData, 'invoiceMatters'> | undefined | null,
    invoiceTimecardsAndExpenses: InvoiceTimecardsAndExpensesDto | undefined | null
) => {
    const invoiceMatterIds = getInvoiceMatterIds(invoiceData);
    const stayingTimecards = (invoiceTimecardsAndExpenses?.invoiceTimecards ?? []).filter(
        (tc) => !isItemMovingOutOfInvoice(tc, invoiceMatterIds)
    );
    const stayingExpenses = (invoiceTimecardsAndExpenses?.invoiceExpenses ?? []).filter(
        (ex) => !isItemMovingOutOfInvoice(ex, invoiceMatterIds)
    );
    return { stayingTimecards, stayingExpenses };
};

const isPaymentCurrencyTotalZero = (invoiceTotals: InvoiceTotals): boolean =>
    Number(invoiceTotals.paymentCurrency?.invoice?.totalWithVat ?? 0) === 0;

export const isInvoiceEffectivelyEmpty = (
    invoiceData: Pick<InvoiceData, 'invoiceMatters'> | undefined | null,
    invoiceTimecardsAndExpenses: InvoiceTimecardsAndExpensesDto | undefined | null,
    invoiceTotals: InvoiceTotals | undefined | null
): boolean => {
    if (!invoiceData || !invoiceTimecardsAndExpenses || !invoiceTotals) {
        return false;
    }
    const { stayingTimecards, stayingExpenses } = getStayingTimecardsAndExpenses(
        invoiceData,
        invoiceTimecardsAndExpenses
    );
    return stayingTimecards.length === 0 && stayingExpenses.length === 0 && isPaymentCurrencyTotalZero(invoiceTotals);
};

/**
 * Unlike {@link isInvoiceEffectivelyEmpty}, items pending a move to another case still count: the review
 * transition is what applies the move, so blocking the review on them would leave the invoice stuck.
 */
export const isInvoiceWithoutItems = (
    invoiceTimecardsAndExpenses: InvoiceTimecardsAndExpensesDto | undefined | null,
    invoiceTotals: InvoiceTotals | undefined | null
): boolean => {
    if (!invoiceTimecardsAndExpenses || !invoiceTotals) {
        return false;
    }
    return (
        (invoiceTimecardsAndExpenses.invoiceTimecards ?? []).length === 0 &&
        (invoiceTimecardsAndExpenses.invoiceExpenses ?? []).length === 0 &&
        isPaymentCurrencyTotalZero(invoiceTotals)
    );
};

/**
 * Builds URL query parameters for debt reminder notification filters.
 * Filters: DEBIT invoices, SENT status, NORMAL/DOUBTFUL receivability,
 * past due date, outstanding balance > 0, not excluded from reporting.
 * @param date - The date to use for due date comparison (defaults to today)
 */
export const buildDebtReminderFilterParams = (date: Date = new Date()): string => {
    const formattedDate = format(date, DEFAULT_DATE_FORMAT);
    const mainFilters = [
        'invoiceTypeCode=in=(DEBIT)',
        'receivabilityTypeCode=in=(DOUBTFUL,NORMAL)',
        'invoiceStatusTypeCode=invoiceremainsunpaid=true',
        `dueDate=duedateispast=${formattedDate}`,
        'excludeFromReporting=isnottrue=true',
    ].join(';');
    const quickFilters = 'invoiceStatusTypeCode=in=(SENT)';
    return `mf=${mainFilters}&qf=${quickFilters}`;
};

/**
 * How many days an invoice is past its due date, as the list's "Days overdue" column and both invoice
 * exports report it. Negative for an invoice that is not due yet, and `null` when there is no due date —
 * there is nothing to be overdue against, so the column stays blank and the export cell stays empty.
 *
 * Calendar days, measured against the viewer's own date, while the backend's `daysOverdue` sort and
 * filter subtract the same two dates against the server's. The two can therefore differ by one across a
 * timezone boundary — by the same day for every row, so the sort order is unaffected, though a row
 * sitting exactly on a filter's threshold can fall on the other side of it for that one day.
 */
export const getDaysOverdue = (dueDate: string | null | undefined): number | null =>
    dueDate ? differenceInCalendarDays(new Date(), parseISO(dueDate)) : null;
